import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';

import CustomDropdown from '@components/CustomDropdown';
import LoaderComponent from '@components/LoaderComponent';
import { toastError, toastSuccess } from '@components/Toast';

import { getAllCountries, getAllTags } from '@store/reducers';
import {
	fetchAllCountry,
	fetchAllSuppliers,
	fetchAllTags,
	fetchSupplierCityByCountry
} from '@store/supplier/action';

import { fetchEnumValues } from '@services/commonService';

import ApiClient from '@lib/apiClient';

import { isValidEmail, isValidURL } from '@utils/Util';

const AddSupplier = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [name, setName] = useState('');
	const [supplierType, setSupplierType] = useState([]);
	const [strengthList, setStrengthList] = useState([]);
	const [contactWebsite, setContactWebsite] = useState('');
	const [contactName, setContactName] = useState('');
	const [contactDesignation, setContactDesignation] = useState('');
	const [contactPhone, setContactPhone] = useState(null);
	const [contactEmail, setContactEmail] = useState('');
	const [officeCountry, setOfficeCountry] = useState(null);
	const [officeState, setOfficeState] = useState(null);
	const [officeCity, setOfficeCity] = useState(null);
	const [officeZip, setOfficeZip] = useState('');
	const [officeAddress, setOfficeAddress] = useState('');
	const [isSameLocation, setIsSameLocation] = useState(null);
	const [factoryCountry, setFactoryCountry] = useState(null);
	const [factoryState, setFactoryState] = useState(null);
	const [factoryCity, setFactoryCity] = useState(null);
	const [factoryZip, setFactoryZip] = useState('');
	const [factoryAddress, setFactoryAddress] = useState('');
	const [partnerShipOwner, setPartnerShipOwner] = useState({});
	const [relationshipManager, setRelationshipManager] = useState({});
	const [userList, setUserList] = useState([]);
	const [suppliers, setSuppliers] = useState([]);
	const [categories, setCategories] = useState([]);
	const [categoryList, setCategoryList] = useState([]);
	const [errors, setErrors] = useState({});

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const tagType = 'SUPPLIER_STRENGTH';
	const basicInfoRef = useRef(null);
	const contactRef = useRef(null);

	const fetchUserList = async () => {
		await ApiClient.GET('getProjectUserSuggestions', '?isNitexUser=true&size=1000')
			.then(({ data }) => {
				if (data) {
					const result = data?.map((item) => {
						return {
							key: item.id,
							value: item.name,
							email: item.email
						};
					});
					setUserList(result);
				}
			})
			.catch(({ response }) => {
				toastError(response.data.message);
			});
	};

	useEffect(() => {
		setIsLoading(true);
		fetchUserList();
		fetchEnumValues('info.nitex.app.supplier.SupplierType', setSuppliers);
		fetchEnumValues('info.nitex.app.supplier.SupplierCategory', setCategories);
		dispatch(fetchAllSuppliers());
		dispatch(fetchAllCountry());
		setIsLoading(false);
	}, []);

	useEffect(() => {
		dispatch(fetchAllTags('', tagType));
	}, []);

	let countries = useSelector(getAllCountries);
	let allTags = useSelector(getAllTags);

	allTags = allTags?.strengthTags?.map((item) => {
		return {
			id: item.id,
			text: item.text,
			value: item.id,
			label: item.text
		};
	});

	const onChangeOfficeCountry = (item) => {
		setOfficeCountry(item);
		if (item.id) {
			setErrors({ ...errors, ['officeCountryError']: undefined });
		}
		dispatch(fetchSupplierCityByCountry(item.id));
		if (isSameLocation) {
			setFactoryCountry(item);
		}
	};

	const onChnageOfficeCity = (e) => {
		setOfficeCity(e.target.value);
		if (isSameLocation) {
			setFactoryCity(e.target.value);
		}
	};

	const onChangeOfficeState = (e) => {
		setOfficeState(e.target.value);
		if (isSameLocation) {
			setFactoryState(e.target.value);
		}
	};

	const onChangeOfficeAddress = (e) => {
		setOfficeAddress(e.target.value);
		if (isSameLocation) {
			setFactoryAddress(e.target.value);
		}
	};

	const onChangeOfficeZip = (e) => {
		setOfficeZip(e.target.value);
		if (isSameLocation) {
			setFactoryZip(e.target.value);
		}
	};

	const onChangeFactoryCountry = (item) => {
		setFactoryCountry(item);
		if (item.id) {
			dispatch(fetchSupplierCityByCountry(item.id));
		}
	};

	const onChangeSupplierType = (selected) => {
		let tags = selected.map((item) => item.id);
		setSupplierType([...tags]);
	};

	const onChangeCategories = (selected) => {
		let tags = selected.map((item) => item.id);
		setCategoryList([...tags]);
	};

	const onChangeTags = (selected) => {
		let tags = selected.map((item) => {
			if (item.__isNew__) {
				return {
					text: item.label
				};
			} else {
				return {
					text: item.label,
					id: item.id
				};
			}
		});
		setStrengthList([...tags]);
	};

	const isValidForm = () => {
		if (name.trim() === '') {
			setErrors({ ...errors, nameError: 'Name is required' });
			basicInfoRef.current.scrollIntoView({ behavior: 'smooth' });
			return false;
		}
		if (supplierType.length === 0) {
			setErrors({ ...errors, supplierTypeError: 'Supplier type is required' });
			basicInfoRef.current.scrollIntoView({ behavior: 'smooth' });
			return false;
		}
		if (categoryList.length === 0) {
			setErrors({ ...errors, categoryTypeError: 'Category is required' });
			basicInfoRef.current.scrollIntoView({ behavior: 'smooth' });
			return false;
		}
		if (contactName.trim() === '') {
			setErrors({ ...errors, contactNameError: 'Contact name is required' });
			basicInfoRef.current.scrollIntoView({ behavior: 'smooth' });
			return false;
		}

		if (contactWebsite) {
			if (!isValidURL(contactWebsite)) {
				setErrors({ ...errors, validWebsiteError: 'Valid website is required' });
				contactRef.current.scrollIntoView({ behavior: 'smooth' });
				return false;
			}
		}

		if (contactDesignation.trim() === '') {
			setErrors({ ...errors, contactDesignationError: 'Designation is required' });
			contactRef.current.scrollIntoView({ behavior: 'smooth' });
			return false;
		}

		if (contactEmail) {
			if (!isValidEmail(contactEmail)) {
				contactRef.current.scrollIntoView({ behavior: 'smooth' });
				setErrors({ ...errors, validEmailError: 'Valid email is required' });
				return false;
			}
		}

		if (contactPhone === null) {
			contactRef.current.scrollIntoView({ behavior: 'smooth' });
			setErrors({ ...errors, contactPhoneError: 'Phone no is required' });
			return false;
		}

		if (officeCountry === null || officeCountry === '') {
			setErrors({ ...errors, officeCountryError: 'Country  is required' });
			return false;
		}
		if (officeCity === null || officeCity === '') {
			setErrors({ ...errors, officeCityError: 'City is required' });
			return false;
		}
		if (officeAddress.trim() === '') {
			setErrors({ ...errors, officeAddressError: 'Address  is required' });
			return false;
		}
		if (isSameLocation === null) {
			setErrors({ ...errors, isSameLocationError: 'Required' });
			return false;
		}
		if (factoryCountry === null || factoryCountry === '') {
			setErrors({ ...errors, factoryCountryError: 'Country  is required' });
			return false;
		}
		if (factoryCity === null || factoryCity === '') {
			setErrors({ ...errors, factoryCityError: 'City is required' });
			return false;
		}
		if (factoryAddress.trim() === '') {
			setErrors({ ...errors, factoryAddressError: 'Address  is required' });
			return false;
		}
		return true;
	};

	const onSubmit = async () => {
		const isValid = isValidForm();
		if (!isValid) return;
		setIsLoading(true);
		const supplierInfo = {
			contactPerson: {
				designation: contactDesignation,
				email: contactEmail,
				name: contactName,
				phone: contactPhone
			},
			factoryLocation: {
				address: factoryAddress,
				city: factoryCity,
				countryId: factoryCountry.id,
				locationType: 'FACTORY',
				state: factoryState,
				zip: factoryZip
			},
			name,
			website: contactWebsite,
			officeLocation: {
				address: officeAddress,
				city: officeCity,
				countryId: officeCountry.id,
				locationType: 'OFFICE',
				state: officeState,
				zip: officeZip
			},
			strengthList,
			supplierCategoryList: categoryList,
			supplierTypeList: supplierType,
			partnershipOwnerId: partnerShipOwner.key,
			relationshipManagerId: relationshipManager.key
		};
		await ApiClient.POST('addSuplier', supplierInfo)
			.then(({ data }) => {
				setIsLoading(false);
				if (data && data.success) {
					toastSuccess(data.message);
					navigate(`/suppliers/view/${data.id}`);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onSameLocation = () => {
		setIsSameLocation(true);
		setFactoryAddress(officeAddress);
		setFactoryCity(officeCity);
		setFactoryCountry(officeCountry);
		setFactoryState(officeState);
		setFactoryZip(officeZip);
	};

	const onNotSameLocation = () => {
		setIsSameLocation(false);
		setFactoryAddress('');
		setFactoryCity(null);
		setFactoryCountry(null);
		setFactoryState(null);
		setFactoryZip('');
	};

	const handleOnBlueEvent = (e, fieldName) => {
		if (e.target.value && e.target.value !== '' && e.target.value !== 'Select') {
			setErrors({ ...errors, [fieldName]: undefined });
		}
	};

	const handleBlurEvent = (fieldName) => {
		if (fieldName === 'supplierTypeError' && supplierType.length > 0) {
			setErrors({ ...errors, [fieldName]: undefined });
		}
		if (fieldName === 'categoryTypeError' && categoryList.length > 0) {
			setErrors({ ...errors, [fieldName]: undefined });
		}
	};

	const onItemClick = (item) => {
		if (item?.title === 'Partnership Owner') {
			setPartnerShipOwner(item);
		}
		if (item?.title === 'Key relationship manager') {
			setRelationshipManager(item);
		}
	};

	return (
		<LoaderComponent loading={isLoading}>
			<section className="form-container">
				<div className="form-header">
					<p className="semibold-16 mb-2 pb-1">Add supplier</p>
				</div>
				<div className="container px-0">
					<div className="row form-row" ref={basicInfoRef}>
						<div className="col-md-12">
							<h2 className="semibold-16 form-title">Factory basic info</h2>
						</div>
						<div className="col-md-12">
							<div className="form-group">
								<label htmlFor="name">Supplier name*</label>
								<input
									type="text"
									id="name"
									name="name"
									onChange={(e) => setName(e.target.value)}
									onBlur={(e) => handleOnBlueEvent(e, 'nameError')}
									autoComplete="off"
								/>
								{errors.nameError && <p className="error">{errors.nameError}</p>}
							</div>
						</div>
						<div className="col-md-12">
							<div className="form-group no-shadow-react-select size36">
								<label htmlFor="supplierType">Supplier type*</label>
								<Select
									isMulti
									onChange={onChangeSupplierType}
									onBlur={() => handleBlurEvent('supplierTypeError')}
									options={suppliers}
								/>
								{errors.supplierTypeError && (
									<p className="error">{errors.supplierTypeError}</p>
								)}
							</div>
						</div>
						<div className="col-md-12">
							<div className="form-group no-shadow-react-select size36">
								<label htmlFor="category">Category*</label>
								<Select
									isMulti
									onChange={onChangeCategories}
									onBlur={() => handleBlurEvent('categoryTypeError')}
									options={categories}
								/>
								{errors.categoryTypeError && (
									<p className="error">{errors.categoryTypeError}</p>
								)}
							</div>
						</div>
						<div className="col-md-12">
							<div className="form-group no-shadow-react-select size36 mb-0">
								<label htmlFor="strengthList">Product strength</label>
								<CreatableSelect isMulti onChange={onChangeTags} options={allTags} />
							</div>
						</div>
					</div>

					<div className="row form-row" ref={contactRef}>
						<div className="col-md-12">
							<h2 className="semibold-16 form-title">Contact</h2>
						</div>
						<div className="col-md-12">
							<div className="form-group">
								<label htmlFor="contactWebsite">Webstie</label>
								<input
									type="text"
									autoComplete="off"
									id="contactWebsite"
									name="contactWebsite"
									placeholder="www.webaddress.com"
									onChange={(e) => {
										setContactWebsite(e.target.value);
										setErrors({ ...errors, validWebsiteError: '' });
									}}
									onBlur={(e) => handleOnBlueEvent(e, 'validWebsiteError')}
								/>
								{errors.validWebsiteError && (
									<p className="error">{errors.validWebsiteError}</p>
								)}
							</div>
						</div>
						<div className="col-md-6 pr-10">
							<div className="form-group">
								<label htmlFor="contactName">Contact person*</label>
								<input
									type="text"
									autoComplete="off"
									id="contactName"
									name="contactName"
									onChange={(e) => setContactName(e.target.value)}
									onBlur={(e) => handleOnBlueEvent(e, 'contactNameError')}
								/>
								{errors.contactNameError && (
									<p className="error">{errors.contactNameError}</p>
								)}
							</div>
						</div>
						<div className="col-md-6 pl-10">
							<div className="form-group">
								<label htmlFor="contactDesignation">Designation*</label>
								<input
									type="text"
									autoComplete="off"
									id="contactDesignation"
									name="contactDesignation"
									onChange={(e) => setContactDesignation(e.target.value)}
									onBlur={(e) => handleOnBlueEvent(e, 'contactDesignationError')}
								/>
								{errors.contactDesignationError && (
									<p className="error">{errors.contactDesignationError}</p>
								)}
							</div>
						</div>
						<div className="col-md-6 pr-10">
							<div className="form-group mb-0">
								<label htmlFor="contactPhone">Phone number*</label>
								<input
									type="number"
									id="contactPhone"
									name="contactPhone"
									onChange={(e) => setContactPhone(e.target.value)}
									onBlur={(e) => handleOnBlueEvent(e, 'contactPhoneError')}
								/>
								{errors.contactPhoneError && (
									<p className="error">{errors.contactPhoneError}</p>
								)}
							</div>
						</div>
						<div className="col-md-6 pl-10">
							<div className="form-group mb-0">
								<label htmlFor="contactEmail">Email</label>
								<input
									type="email"
									id="contactEmail"
									name="contactEmail"
									onChange={(e) => {
										setContactEmail(e.target.value);
										setErrors({ ...errors, validEmailError: '' });
									}}
									onBlur={(e) => handleOnBlueEvent(e, 'validEmailError')}
								/>
								{errors.validEmailError && <p className="error">{errors.validEmailError}</p>}
							</div>
						</div>
					</div>
					<div className="row form-row">
						<div className="col-md-12">
							<h2 className="semibold-16 form-title">Office location</h2>
						</div>
						<div className="col-md-6 pr-10">
							<label htmlFor="collection">Country*</label>
							<div className="form-group size36">
								<CustomDropdown
									type="addItem"
									onItemClick={onChangeOfficeCountry}
									items={countries.map((item) => ({
										id: item.id,
										value: item.name,
										key: item.name
									}))}
									selectedItem={officeCountry?.value}
								/>
								{errors.officeCountryError && (
									<p className="error">{errors.officeCountryError}</p>
								)}
							</div>
						</div>
						<div className="col-md-6 pl-10">
							<div className="form-group size36">
								<label htmlFor="officeState">State</label>
								<input
									type="text"
									autoComplete="off"
									name="officeState"
									id="officeState"
									onChange={(e) => onChangeOfficeState(e)}
								/>
							</div>
						</div>
						<div className="col-md-6 pr-10">
							<div className="form-group size36">
								<label htmlFor="officeCity">City*</label>
								<input
									type="text"
									autoComplete="off"
									name="officeCity"
									id="officeCity"
									onChange={(e) => onChnageOfficeCity(e)}
									onBlur={(e) => handleOnBlueEvent(e, 'officeCityError')}
								/>
								{errors.officeCityError && <p className="error">{errors.officeCityError}</p>}
							</div>
						</div>
						<div className="col-md-6 pl-10">
							<div className="form-group size36">
								<label htmlFor="officeZip">Zip</label>
								<input
									type="text"
									autoComplete="off"
									id="officeZip"
									name="officeZip"
									placeholder={'0000'}
									onChange={(e) => onChangeOfficeZip(e)}
								/>
							</div>
						</div>
						<div className="col-md-12">
							<div className="form-group">
								<label htmlFor="officeAddress">Address*</label>
								<textarea
									name="officeAddress"
									id="officeAddress"
									placeholder="Write remarks"
									defaultValue={''}
									onChange={(e) => onChangeOfficeAddress(e)}
									onBlur={(e) => handleOnBlueEvent(e, 'officeAddressError')}
								/>
								{errors.officeAddressError && (
									<p className="error">{errors.officeAddressError}</p>
								)}
							</div>
						</div>
						<div className="col-md-12">
							<div className="form-group radio-component mb-0">
								<p className="price-type regular-14">
									Is office location &amp; factory location same?*
								</p>
								<input
									type="radio"
									name="isSameLocation"
									value={true}
									checked={isSameLocation === true}
									onChange={() => onSameLocation()}
								/>
								<label htmlFor="yes">Yes</label>
								<input
									type="radio"
									name="isSameLocation"
									value={false}
									checked={isSameLocation === false}
									onChange={() => onNotSameLocation()}
									onBlur={(e) => handleOnBlueEvent(e, 'isSameLocationError')}
								/>
								<label htmlFor="no">No</label>
							</div>
							{errors.isSameLocationError && (
								<p className="error">{errors.isSameLocationError}</p>
							)}
						</div>
					</div>
					{!isSameLocation && isSameLocation !== null && (
						<div className="row form-row">
							<div className="col-md-12">
								<h2 className="semibold-16 form-title">Factory location</h2>
							</div>
							<div className="col-md-6 pr-10">
								<label htmlFor="collection">Country*</label>
								<div className="form-group size36">
									<CustomDropdown
										type="addItem"
										title={'Country'}
										onItemClick={onChangeFactoryCountry}
										items={countries.map((item) => ({
											id: item.id,
											value: item.name,
											key: item.name
										}))}
										selectedItem={factoryCountry?.value}
									/>
									{errors.factoryCountryError && (
										<p className="error">{errors.factoryCountryError}</p>
									)}
								</div>
							</div>
							<div className="col-md-6 pl-10">
								<div className="form-group size36">
									<label htmlFor="factoryState">State</label>
									<input
										type="text"
										autoComplete="off"
										name="factoryState"
										id="factoryState"
										value={factoryState}
										onChange={(e) => setFactoryState(e.target.value)}
									/>
								</div>
							</div>
							<div className="col-md-6 pr-10">
								<div className="form-group size36">
									<label htmlFor="factoryCity">City*</label>
									<input
										type="text"
										autoComplete="off"
										name="factoryCity"
										id="factoryCity"
										value={factoryCity}
										onChange={(e) => setFactoryCity(e.target.value)}
										onBlur={(e) => handleOnBlueEvent(e, 'factoryCityError')}
									/>
									{errors.factoryCityError && (
										<p className="error">{errors.factoryCityError}</p>
									)}
								</div>
							</div>
							<div className="col-md-6 pl-10">
								<div className="form-group size36">
									<label htmlFor="factoryZip">Zip</label>
									<input
										type="text"
										autoComplete="off"
										id="factoryZip"
										name="factoryZip"
										placeholder={'0000'}
										value={factoryZip}
										onChange={(e) => setFactoryZip(e.target.value)}
									/>
								</div>
							</div>
							<div className="col-md-12">
								<div className="form-group mb-0">
									<label htmlFor="factoryAddress">Address*</label>
									<textarea
										name="factoryAddress"
										id="factoryAddress"
										placeholder="Write remarks"
										defaultValue={''}
										value={factoryAddress}
										onChange={(e) => setFactoryAddress(e.target.value)}
										onBlur={(e) => handleOnBlueEvent(e, 'factoryAddressError')}
									/>
									{errors.factoryAddressError && (
										<p className="error">{errors.factoryAddressError}</p>
									)}
								</div>
							</div>
						</div>
					)}

					<div className="row form-row">
						<div className="col-md-12">
							<h2 className="semibold-16 form-title">Account owner & POC</h2>
						</div>
						<div className="col-md-12">
							<div className="form-group">
								<CustomDropdown
									type="addItem"
									title={'Partnership Owner'}
									onItemClick={onItemClick}
									items={userList}
									selectedItem={partnerShipOwner.value}
								/>
							</div>
						</div>
						<div className="col-md-12">
							<div className="form-group">
								<CustomDropdown
									type="addItem"
									title={'Key relationship manager'}
									onItemClick={onItemClick}
									items={userList}
									selectedItem={relationshipManager.value}
								/>
							</div>
						</div>
					</div>

					<div className="row">
						<div className="col-md-12 mb-3 text-right">
							<button className="button outline size32 mr-2">Reset</button>

							<button className="button size32 ml-1" onClick={onSubmit}>
								Submit
							</button>
						</div>
					</div>
				</div>
			</section>
		</LoaderComponent>
	);
};

export default AddSupplier;
